import type { CombinedMonth } from "@dentacore/shared-types";

import { LocationBadge } from "./location-badge";
import styles from "./combined-month.module.css";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/**
 * **The month** (CF-D13): the product's grid, Sunday first, each day its
 * total. With several locations a day also gets a thin bar split by location
 * (in their badge hues) and the three busiest locations' codes with their
 * counts, then "+N". Counted on the server with the filters applied, because a
 * month of ten locations is too many rows to send. A day no shown location
 * opens says so rather than showing a zero. Clicking a day opens it.
 */
export function CombinedMonthView({
  month,
  hiddenBranches,
  today,
  onOpenDay,
  onPrevious,
  onNext,
}: {
  month: CombinedMonth;
  hiddenBranches: readonly string[];
  today: string;
  onOpenDay: (date: string) => void;
  onPrevious: () => void;
  onNext: () => void;
}): React.JSX.Element {
  const locations = month.locations.filter(
    (location) => !hiddenBranches.includes(location.branch.id),
  );
  const combined = month.locations.length > 1;
  const dates = (month.locations[0]?.days ?? []).map((day) => day.date);
  const total = locations.reduce(
    (sum, location) => sum + location.days.reduce((s, day) => s + day.count, 0),
    0,
  );

  return (
    <div className={styles.month} data-testid="month-view" data-month={month.month}>
      <div className={styles.bar}>
        <button
          type="button"
          className={styles.step}
          onClick={onPrevious}
          data-testid="month-previous"
        >
          ← Previous
        </button>
        <span className={styles.title} data-testid="month-title">
          {titleOf(month.month)}
        </span>
        <button type="button" className={styles.step} onClick={onNext} data-testid="month-next">
          Next →
        </button>
        <span className={styles.total}>
          {total === 1 ? "1 appointment this month" : `${String(total)} appointments this month`}
        </span>
      </div>

      <div className={styles.weekdays}>
        {WEEKDAYS.map((name) => (
          <span key={name}>{name}</span>
        ))}
      </div>

      <div className={styles.grid}>
        {Array.from({ length: month.leadingBlanks }, (_, index) => (
          <span key={`blank-${String(index)}`} className={styles.blank} aria-hidden="true" />
        ))}

        {dates.map((date) => {
          const by = locations
            .map((location) => ({
              location,
              day: location.days.find((day) => day.date === date),
            }))
            .filter((entry) => entry.day !== undefined);
          const isOpen = by.some((entry) => entry.day?.isOpen === true);
          const count = by.reduce((sum, entry) => sum + (entry.day?.count ?? 0), 0);
          const busiest = by
            .filter((entry) => (entry.day?.count ?? 0) > 0)
            .sort((a, b) => (b.day?.count ?? 0) - (a.day?.count ?? 0));
          const dayOfMonth = Number(date.slice(8, 10));

          return (
            <button
              key={date}
              type="button"
              className={[
                styles.cell,
                date === today ? styles.today : "",
                isOpen ? "" : styles.shut,
              ].join(" ")}
              onClick={() => onOpenDay(date)}
              aria-label={`${String(dayOfMonth)}, ${isOpen ? `${String(count)} appointments` : "closed"}`}
              data-card=""
              data-testid="month-day"
              data-date={date}
              data-count={count}
            >
              <span className={styles.day}>{dayOfMonth}</span>
              {!isOpen ? (
                <span className={styles.closed}>closed</span>
              ) : (
                <>
                  <span className={styles.count}>
                    {count}
                    <small> {count === 1 ? "appt" : "appts"}</small>
                  </span>
                  {combined && count > 0 && (
                    <>
                      <span className={styles.split} aria-hidden="true">
                        {busiest.map((entry) => (
                          <i
                            key={entry.location.branch.id}
                            style={{
                              width: `${String(((entry.day?.count ?? 0) / count) * 100)}%`,
                              background: `var(--c-loc-${String(entry.location.branch.hue % 10)})`,
                            }}
                          />
                        ))}
                      </span>
                      <span className={styles.top}>
                        {busiest.slice(0, 3).map((entry) => (
                          <span key={entry.location.branch.id} className={styles.topItem}>
                            <LocationBadge
                              code={entry.location.branch.code}
                              hue={entry.location.branch.hue}
                              name={entry.location.branch.name}
                              size="small"
                            />
                            <em>{entry.day?.count}</em>
                          </span>
                        ))}
                        {busiest.length > 3 && (
                          <em className={styles.more}>+{busiest.length - 3}</em>
                        )}
                      </span>
                    </>
                  )}
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function titleOf(month: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    month: "long",
    year: "numeric",
  }).format(new Date(`${month}-01T12:00:00.000Z`));
}
